import '../../styles/theme.css';

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon = null,
  disabled = false,
  children,
  onClick,
  type = 'button',
  className = '',
  style = {},
  ...props
}) {
  const baseStyle = {
    fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
    fontWeight: 700,
    borderRadius: '12px',
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.15s ease',
    width: fullWidth ? '100%' : 'auto',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const variantStyles = {
    primary: {
      background: 'var(--sd-teal, #3ACFCF)',
      color: 'var(--sd-ground, #08090B)',
      border: '1px solid var(--sd-teal, #3ACFCF)',
    },
    secondary: {
      background: 'var(--sd-panel-2, #0E1116)',
      color: 'var(--sd-ink, #EDEFF2)',
      border: '1px solid var(--sd-line-2, #1C2229)',
    },
    accent: {
      background: 'var(--sd-teal, #3ACFCF)',
      color: 'var(--sd-ground, #08090B)',
      border: '1px solid var(--sd-teal, #3ACFCF)',
    },
    amber: {
      background: 'var(--sd-amber, #E0A458)',
      color: 'var(--sd-ground, #08090B)',
      border: '1px solid var(--sd-amber, #E0A458)',
    },
    danger: {
      background: 'var(--sd-rose, #E4738A)',
      color: 'var(--sd-ground, #08090B)',
      border: '1px solid var(--sd-rose, #E4738A)',
    },
    outline: {
      background: 'transparent',
      color: 'var(--sd-teal, #3ACFCF)',
      border: '1px solid var(--sd-teal, #3ACFCF)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--sd-dim, #6E7982)',
      border: '1px solid transparent',
    },
  };

  const sizeStyles = {
    sm: { padding: '6px 12px', fontSize: '0.875rem', minHeight: '44px', minWidth: '44px' },
    md: { padding: '10px 20px', fontSize: '1rem', minHeight: '44px', minWidth: '44px' },
    lg: { padding: '14px 28px', fontSize: '1.125rem', minHeight: '52px' },
    thumb: { padding: '16px 24px', fontSize: '1.2rem', minHeight: '56px', borderRadius: '14px' },
    tv: { padding: '18px 36px', fontSize: '1.35rem', minHeight: '64px', borderRadius: '14px' },
  };

  const combinedStyle = {
    ...baseStyle,
    ...(variantStyles[variant] || variantStyles.primary),
    ...(sizeStyles[size] || sizeStyles.md),
    ...style,
  };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={combinedStyle}
      className={`sd-button ${className}`}
      {...props}
    >
      {icon && <span className="sd-button-icon">{icon}</span>}
      <span>{children}</span>
    </button>
  );
}

export function Card({
  variant = 'default',
  padding = '24px',
  children,
  className = '',
  style = {},
  onClick,
  ...props
}) {
  const baseStyle = {
    borderRadius: '16px',
    boxSizing: 'border-box',
    transition: 'all 0.15s ease',
    padding: padding,
    color: 'var(--sd-ink, #EDEFF2)',
    fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
  };

  const variantStyles = {
    default: {
      background: 'var(--sd-panel, #0D1015)',
      border: '1px solid var(--sd-line, #1A1F25)',
    },
    tv: {
      background: 'var(--sd-panel, #0D1015)',
      border: '1px solid var(--sd-line, #1A1F25)',
    },
    elevated: {
      background: 'var(--sd-panel-2, #0E1116)',
      border: '1px solid var(--sd-line-2, #1C2229)',
    },
    glass: {
      background: 'rgba(13, 16, 21, 0.85)',
      border: '1px solid var(--sd-line, #1A1F25)',
    },
    interactive: {
      background: 'var(--sd-panel, #0D1015)',
      border: '1px solid var(--sd-line, #1A1F25)',
      cursor: 'pointer',
    },
    active: {
      background: 'rgba(58, 207, 207, 0.08)',
      border: '1px solid var(--sd-teal, #3ACFCF)',
    },
  };

  const combinedStyle = {
    ...baseStyle,
    ...(variantStyles[variant] || variantStyles.default),
    ...style,
  };

  return (
    <div
      onClick={onClick}
      style={combinedStyle}
      className={`sd-card ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function Badge({
  variant = 'primary',
  size = 'md',
  children,
  icon = null,
  className = '',
  style = {},
}) {
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontWeight: 600,
    borderRadius: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
  };

  const variantStyles = {
    primary: { background: 'rgba(58, 207, 207, 0.12)', color: 'var(--sd-teal, #3ACFCF)', border: '1px solid rgba(58, 207, 207, 0.3)' },
    cyan: { background: 'rgba(58, 207, 207, 0.12)', color: 'var(--sd-teal, #3ACFCF)', border: '1px solid rgba(58, 207, 207, 0.3)' },
    rose: { background: 'rgba(228, 115, 138, 0.12)', color: 'var(--sd-rose, #E4738A)', border: '1px solid rgba(228, 115, 138, 0.3)' },
    amber: { background: 'rgba(224, 164, 88, 0.12)', color: 'var(--sd-amber, #E0A458)', border: '1px solid rgba(224, 164, 88, 0.3)' },
    emerald: { background: 'rgba(143, 199, 160, 0.12)', color: 'var(--sd-sage, #8FC7A0)', border: '1px solid rgba(143, 199, 160, 0.3)' },
    green: { background: 'rgba(143, 199, 160, 0.12)', color: 'var(--sd-sage, #8FC7A0)', border: '1px solid rgba(143, 199, 160, 0.3)' },
    outline: { background: 'transparent', color: 'var(--sd-dim, #6E7982)', border: '1px solid var(--sd-line-2, #1C2229)' },
    dark: { background: 'var(--sd-panel-2, #0E1116)', color: 'var(--sd-dim, #6E7982)', border: '1px solid var(--sd-line, #1A1F25)' },
  };

  const sizeStyles = {
    sm: { padding: '2px 8px', fontSize: '0.75rem' },
    md: { padding: '4px 12px', fontSize: '0.85rem' },
    lg: { padding: '6px 16px', fontSize: '0.95rem' },
  };

  return (
    <span style={{ ...baseStyle, ...variantStyles[variant], ...sizeStyles[size], ...style }} className={`sd-badge ${className}`}>
      {icon && <span>{icon}</span>}
      {children}
    </span>
  );
}

export function Input({
  label,
  error,
  icon,
  helperText,
  value,
  onChange,
  placeholder,
  maxLength,
  uppercase = false,
  className = '',
  style = {},
  id,
  ...props
}) {
  const inputId = id || (label ? `input-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }} className={className}>
      {label && (
        <label htmlFor={inputId} style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--sd-dim, #6E7982)' }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {icon && (
          <span style={{ position: 'absolute', left: '14px', color: 'var(--sd-dim, #6E7982)', display: 'flex' }}>
            {icon}
          </span>
        )}
        <input
          id={inputId}
          value={value}
          onChange={(e) => {
            if (uppercase && e.target.value) {
              e.target.value = e.target.value.toUpperCase();
            }
            onChange(e);
          }}
          placeholder={placeholder}
          maxLength={maxLength}
          style={{
            width: '100%',
            padding: icon ? '12px 14px 12px 42px' : '12px 16px',
            fontSize: '15px',
            background: 'var(--sd-panel-2, #0E1116)',
            color: 'var(--sd-ink, #EDEFF2)',
            border: error ? '1px solid var(--sd-rose, #E4738A)' : '1px solid var(--sd-line-2, #1C2229)',
            borderRadius: '12px',
            outline: 'none',
            boxSizing: 'border-box',
            textTransform: uppercase ? 'uppercase' : 'none',
            letterSpacing: uppercase ? '0.1em' : 'normal',
            fontWeight: uppercase ? 700 : 400,
            minHeight: '48px',
            fontFamily: uppercase ? "var(--sd-font-mono, 'IBM Plex Mono'), monospace" : "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
            ...style,
          }}
          {...props}
        />
      </div>
      {error && <span style={{ fontSize: '0.8rem', color: 'var(--sd-rose, #E4738A)', fontWeight: 600 }}>{error}</span>}
      {helperText && !error && <span style={{ fontSize: '0.8rem', color: 'var(--sd-dim, #6E7982)' }}>{helperText}</span>}
    </div>
  );
}

export function Toggle({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  icon = null,
  style = {},
  id,
}) {
  const toggleId = id || `toggle-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px',
        background: 'var(--sd-panel-2, #0E1116)',
        borderRadius: '12px',
        border: '1px solid var(--sd-line-2, #1C2229)',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        minHeight: '48px',
        ...style,
      }}
      onClick={() => !disabled && onChange(!checked)}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {icon && <span style={{ fontSize: '1.2rem' }}>{icon}</span>}
        <div>
          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--sd-ink, #EDEFF2)' }}>{label}</div>
          {description && <div style={{ fontSize: '0.8rem', color: 'var(--sd-dim, #6E7982)' }}>{description}</div>}
        </div>
      </div>
      <button
        id={toggleId}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) onChange(!checked);
        }}
        style={{
          width: '48px',
          height: '26px',
          background: checked ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-panel, #0D1015)',
          borderRadius: '9999px',
          border: '1px solid var(--sd-line, #1A1F25)',
          position: 'relative',
          padding: '2px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'background 0.15s ease',
        }}
      >
        <span
          style={{
            display: 'block',
            width: '20px',
            height: '20px',
            background: checked ? 'var(--sd-ground, #08090B)' : 'var(--sd-dim, #6E7982)',
            borderRadius: '50%',
            transform: checked ? 'translateX(22px)' : 'translateX(0)',
            transition: 'transform 0.15s ease',
          }}
        />
      </button>
    </div>
  );
}

export function Avatar({
  emoji = '🎵',
  size = 'md',
  selected = false,
  onClick,
  style = {},
}) {
  const sizeMap = {
    sm: { width: '36px', height: '36px', fontSize: '1.1rem' },
    md: { width: '48px', height: '48px', fontSize: '1.5rem' },
    lg: { width: '64px', height: '64px', fontSize: '2rem' },
    xl: { width: '80px', height: '80px', fontSize: '2.5rem' },
  };

  const dimensions = sizeMap[size] || sizeMap.md;

  return (
    <div
      onClick={onClick}
      style={{
        ...dimensions,
        borderRadius: '50%',
        background: selected ? 'rgba(58, 207, 207, 0.15)' : 'var(--sd-panel-2, #0E1116)',
        border: selected ? '2px solid var(--sd-teal, #3ACFCF)' : '1px solid var(--sd-line-2, #1C2229)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        userSelect: 'none',
        ...style,
      }}
    >
      {emoji}
    </div>
  );
}

export function SoundGateBanner({ soundEnabled, onEnableSound }) {
  if (soundEnabled) {
    return (
      <div
        data-testid="sound-gate-active"
        style={{
          background: 'rgba(143, 199, 160, 0.12)',
          border: '1px solid rgba(143, 199, 160, 0.35)',
          color: 'var(--sd-sage, #8FC7A0)',
          padding: '12px 20px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontWeight: 600,
          fontSize: '1rem',
          marginBottom: '20px',
          fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
        }}
      >
        <span>🔊</span> Audio System Ready & Enabled
      </div>
    );
  }

  return (
    <div
      className="sound-gate-banner"
      data-testid="sound-gate-banner"
      style={{
        background: 'var(--sd-amber, #E0A458)',
        color: 'var(--sd-ground, #08090B)',
        padding: '16px 24px',
        borderRadius: '16px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: '1.8rem' }}>🔊</span>
        <div>
          <div style={{ fontWeight: 800, fontSize: '1.2rem', color: 'var(--sd-ground, #08090B)' }}>
            Audio Required for Host Screen
          </div>
          <div style={{ fontSize: '0.95rem', color: 'rgba(8, 9, 11, 0.8)', fontWeight: 600 }}>
            iOS/Safari & browsers require user gesture to enable Web Audio playback
          </div>
        </div>
      </div>
      <button
        type="button"
        className="sound-gate-button"
        data-testid="enable-sound-btn"
        onClick={onEnableSound}
        style={{
          background: 'var(--sd-ground, #08090B)',
          color: 'var(--sd-ink, #EDEFF2)',
          border: '1px solid var(--sd-line, #1A1F25)',
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 700,
          borderRadius: '12px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          minHeight: '44px',
        }}
      >
        Tap to enable sound
      </button>
    </div>
  );
}

export function HeadphonesAffordance({ visible = true, message = 'headphones recommended' }) {
  if (!visible) return null;

  return (
    <div
      className="headphones-affordance"
      data-testid="headphones-affordance"
      style={{
        background: 'rgba(58, 207, 207, 0.06)',
        border: '1px solid rgba(58, 207, 207, 0.22)',
        color: '#9FB4B8',
        padding: '10px 16px',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '16px',
        fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
      }}
    >
      <svg width="17" height="17" viewBox="0 0 18 18" fill="none" style={{ flex: 'none' }} aria-hidden="true">
        <path d="M3 10.5V9a6 6 0 0112 0v1.5" stroke="var(--sd-teal, #3ACFCF)" strokeWidth="1.5" strokeLinecap="round"/>
        <rect x="1.9" y="10.2" width="3.4" height="5.4" rx="1.7" stroke="var(--sd-teal, #3ACFCF)" strokeWidth="1.5"/>
        <rect x="12.7" y="10.2" width="3.4" height="5.4" rx="1.7" stroke="var(--sd-teal, #3ACFCF)" strokeWidth="1.5"/>
      </svg>
      <span>{message}</span>
    </div>
  );
}

export default {
  Button,
  Card,
  Badge,
  Input,
  Toggle,
  Avatar,
  SoundGateBanner,
  HeadphonesAffordance,
};
